import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Ban, Leaf, PhoneOff, UserRound, XCircle } from "lucide-react";
import { leadPatch, leadStatusChange, type PickableLeadStatus } from "@shared/schemas/leads";
import { validateLead } from "@shared/validation/leads";
import { blockingIssues, evaluation, parseInput, type ValidationAction } from "@shared/validation/issues";
import { errorMessage } from "@web/lib/api";
import { useFormValidation } from "@web/lib/formValidation";
import { membersQuery } from "@web/lib/queries";
import { leadApi, type LeadDetail } from "@web/lib/queries-leads";
import { BottomSheet, Button, Picker, Row, RowGroup, Stack, Text, TextField, UnsavedChanges, ValidationSummary, useToast } from "@web/ui";

export type MoreMode = "menu" | "lost" | "dnc" | "assign";

/** The lead's More sheet (SPEC §6.1 item 4): Lost (reason) · Nurture · No contact · DNC (confirm) · Assign. Also opened directly by the status Picker. */
export function MoreSheet({ lead, mode, onClose, onMode }: { lead: LeadDetail; mode: MoreMode; onClose: () => void; onMode: (m: MoreMode) => void }) {
  const toast = useToast();
  const members = useQuery(membersQuery);
  const [reason, setReason] = useState("");
  const [assignee, setAssignee] = useState(lead.assignedMemberId ?? "");
  const [failure, setFailure] = useState<string | null>(null);
  const closed = lead.status === "converted";
  const record = { ...lead, contact: undefined, secondaryContact: undefined };
  const entity = { type: "lead" as const, id: lead.id };
  const assignment = parseInput(leadPatch, { assignedMemberId: assignee || null }, entity, "lead.save");
  function statusFindings(status: PickableLeadStatus, lostReason?: string) {
    const parsed = parseInput(leadStatusChange, { status, lostReason }, entity, "lead.transition");
    return evaluation([...parsed.issues, ...validateLead(record, { action: "lead.transition", transition: { status, lostReason } }).issues], false);
  }
  const local = mode === "lost" ? statusFindings("lost", reason) : mode === "dnc" ? statusFindings("dnc") : mode === "assign"
    ? evaluation([...assignment.issues, ...validateLead({ ...record, assignedMemberId: assignee || null }, { action: "lead.save", before: record, changedFields: ["assignedMemberId"] }).issues], false)
    : evaluation([], false);
  const validation = useFormValidation(local), { busy } = validation;
  const close = () => validation.edit(onClose);
  const edit = (change: () => void) => validation.edit(() => { change(); setFailure(null); });
  const feedback = <><UnsavedChanges dirty={(mode === "lost" && Boolean(reason)) || (mode === "assign" && assignee !== (lead.assignedMemberId ?? ""))} pending={busy} /><ValidationSummary result={validation.result} local unavailable={validation.unavailable} />{failure && <div role="alert"><Text tone="danger">{failure}</Text></div>}</>;

  async function run(action: ValidationAction, fn: () => Promise<unknown>, done: string) {
    if (!validation.begin(action)) return;
    setFailure(null);
    try {
      await fn();
      toast.success(done);
      onClose();
    } catch (e) {
      validation.reject(e); setFailure(errorMessage(e));
    } finally {
      validation.finish();
    }
  }
  const setStatus = (status: PickableLeadStatus, done: string, lostReason?: string) => {
    const parsed = parseInput(leadStatusChange, { status, lostReason }, entity, "lead.transition");
    const findings = statusFindings(status, lostReason);
    if (!parsed.success || blockingIssues(findings, "lead.transition").length) { validation.report(findings.issues); return; }
    return run("lead.transition", () => leadApi.setStatus(lead.id, parsed.data.status, parsed.data.lostReason), done);
  };

  if (mode === "lost")
    return (
      <BottomSheet
        open
        onClose={close}
        title="Mark lost"
        footer={
          <Button variant="destructive" loading={busy} onClick={() => setStatus("lost", "Marked lost", reason)}>
            Mark lost
          </Button>
        }
      >
        <Stack gap={3}>{feedback}<TextField label="Reason" placeholder="Budget, date taken, chose another venue…" value={reason} {...validation.field("lostReason")} onChange={(e) => edit(() => setReason(e.target.value))} autoFocus /></Stack>
      </BottomSheet>
    );

  if (mode === "dnc")
    return (
      <BottomSheet
        open
        onClose={close}
        title="Do not contact"
        footer={
          <Button variant="destructive" loading={busy} onClick={() => setStatus("dnc", "Marked do-not-contact")}>
            Confirm DNC
          </Button>
        }
      >
        <Stack gap={3}>{feedback}<Text variant="callout" tone="secondary">
          Flags the contact as do-not-contact, stops every automated message, and blocks texts and emails from here. Only transactional messages for an active booking still go out.
        </Text></Stack>
      </BottomSheet>
    );

  if (mode === "assign")
    return (
      <BottomSheet
        open
        onClose={close}
        title="Assign lead"
        footer={
          <Button loading={busy} onClick={() => { if (assignment.success) void run("lead.save", () => leadApi.patchLead(lead.id, assignment.data), assignee ? "Lead assigned" : "Unassigned"); }}>
            Save
          </Button>
        }
      >
        <Stack gap={3}>{feedback}<Picker label="Assignee" placeholder="Unassigned" value={assignee} {...validation.field("assignedMemberId")} onChange={value => edit(() => setAssignee(value))} options={(members.data ?? []).filter((m) => m.active || m.id === lead.assignedMemberId).map((m) => ({ value: m.id, label: m.name }))} /></Stack>
      </BottomSheet>
    );

  return (
    <BottomSheet open onClose={close} title="Lead">
      <Stack gap={3}>
        {(busy || failure || validation.result.issues.length > 0) && feedback}
        {closed && (
          <Text variant="callout" tone="secondary">
            This lead is converted — work the booking instead. You can still reassign it.
          </Text>
        )}
        <RowGroup>
          {!closed && lead.status !== "lost" && <Row icon={<XCircle aria-hidden size={22} />} title="Mark lost" subtitle="Needs a reason" onClick={busy ? undefined : () => edit(() => onMode("lost"))} />}
          {!closed && lead.status !== "nurture" && <Row icon={<Leaf aria-hidden size={22} />} title="Nurture" subtitle="Long-term follow-up" onClick={busy ? undefined : () => setStatus("nurture", "Moved to nurture")} />}
          {!closed && lead.status !== "no_contact" && <Row icon={<PhoneOff aria-hidden size={22} />} title="No contact" subtitle="Couldn't reach them" onClick={busy ? undefined : () => setStatus("no_contact", "Marked no contact")} />}
          {!closed && lead.status !== "dnc" && <Row icon={<Ban aria-hidden size={22} />} title="Do not contact" subtitle="Blocks all non-transactional messages" onClick={busy ? undefined : () => edit(() => onMode("dnc"))} />}
          <Row icon={<UserRound aria-hidden size={22} />} title="Assign to member" subtitle={lead.assignedMemberId ? (members.data?.find((m) => m.id === lead.assignedMemberId)?.name ?? "Assigned") : "Unassigned"} onClick={busy ? undefined : () => edit(() => onMode("assign"))} />
        </RowGroup>
      </Stack>
    </BottomSheet>
  );
}
